import React, { useContext, useMemo, useRef, useState } from "react";
import {
    WrapperAdvancedSearch,
    SectionHeading,
    TableCustom,
} from "../../components/common";
import { ServerRequest } from "../../utils/apiweb";
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis";
import { useLista } from "../../components/hooks/useLista";
import CuentasPruebaEditModal from "./components/CuentasPruebaEditModal";
import { ManagedContext } from "../../context/custom";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";

const CuentasPruebaView = () => {
    const [data, setData] = useState([]);
    const [tableVisible, setTableVisible] = useState(false);
    const [editState, setEditState] = useState({ item: null, mode: null });

    const { setIsLoading, showMessageModal } = useContext(ManagedContext);

    const searchRef = useRef();

    const [_, getRowLista] = useLista({
        listas: ["TipoTributo"],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: {
            key: "CuentasPruebaListas",
            timeout: 0,
        },
    });

    const filteredData = useMemo(() => {
        const idTipoTributo = searchRef.current?.filters?.idTipoTributo ?? 0;
        return data.filter((x) => x.idTipoTributo === idTipoTributo);
    }, [data, searchRef]);

    const onNoSuccess = (response) => {
        response
            .json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setIsLoading(false);
            })
            .catch((error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando respuesta: " + error
                );
                setIsLoading(false);
            });
    };

    const onError = (error) => {
        ShowToastMessage(
            ALERT_TYPE.ALERT_ERROR,
            "Error procesando solicitud: " + error.message
        );
        setIsLoading(false);
    };

    const onSearch = () => {
        setIsLoading(true);

        const onSuccess = (response) => {
            response.json().then((data) => {
                setData(data);
                setIsLoading(false);
                setTableVisible(true);
            });
        };

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CUENTA_PRUEBA,
            null,
            null,
            onSuccess,
            onNoSuccess,
            onError
        );
    };

    const onAdd = () => {
        const idTipoTributo = searchRef.current?.filters?.idTipoTributo ?? 0;
        if (idTipoTributo < 1) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, "Debe seleccionar un tipo de tributo");
            return;
        }
        setEditState({
            mode: OPERATION_MODE.NEW,
            item: {
                id: -Date.now(),
                idTipoTributo: idTipoTributo,
                idCuenta: 0,
                comentario: "",
            },
        });
    };

    const onView = (row) => {
        setEditState({
            mode: OPERATION_MODE.VIEW,
            item: row,
        });
    };

    const onModify = (row) => {
        setEditState({
            mode: OPERATION_MODE.EDIT,
            item: row,
        });
    };

    const onRemove = ({ id }) => {
        const cuenta = data.find((x) => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar la cuenta de prueba ${cuenta.numeroCuenta}?`,
            onConfirm: () => {
                setIsLoading(true);
                ServerRequest(
                    REQUEST_METHOD.DELETE,
                    null,
                    true,
                    APIS.URLS.CUENTA_PRUEBA,
                    `/${id}`,
                    null,
                    () => {
                        setData((prev) => prev.filter((x) => x.id !== id));
                        setIsLoading(false);
                        setEditState({});
                    },
                    onNoSuccess,
                    onError
                );
            },
        });
    };

    const onModalAccept = () => {
        const isNew = editState.mode === OPERATION_MODE.NEW;
        setIsLoading(true);

        ServerRequest(
            isNew ? REQUEST_METHOD.POST : REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA_PRUEBA,
            isNew ? null : `/${editState.item.id}`,
            editState.item,
            () => {
                onSearch();
                setEditState({
                    mode: null,
                    item: null,
                });
            },
            onNoSuccess,
            onError
        );
    };

    const onModalCancel = () => {
        setEditState({
            mode: null,
            item: null,
        });
    };

    return (
        <>
            <SectionHeading
                titles={[
                    { title: "Emisión" },
                    { title: "Cuentas de prueba" },
                ]}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Tipo de tributo",
                            field: "idTipoTributo",
                            type: "list",
                            lista: "TipoTributo",
                            size: 4,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        className={"TableCustomBase m-top-20"}
                        data={filteredData}
                        messageEmpty="No se encontraron resultados"
                        columns={[
                            {
                                Header: "Número de cuenta",
                                accessor: "numeroCuenta",
                                width: "45%",
                            },
                            {
                                Header: "Comentario",
                                accessor: "comentario",
                                width: "45%",
                                Cell: ({ row }) => row.original.comentario,
                            },
                        ]}
                        avmr={{ onAdd, onView, onModify, onRemove }}
                        editCode="cuenta-prueba_edit"
                    />
                )}

                {editState.item && (
                    <CuentasPruebaEditModal
                        {...editState}
                        setItem={(item) => setEditState((prev) => ({ ...prev, item }))}
                        onAccept={onModalAccept}
                        onCancel={onModalCancel}
                    />
                )}
            </section>
        </>
    );
};

export default CuentasPruebaView;
